// 261023 E3-P3ka (mockup Rev 3) — mỗi tập MỘT dòng ở bước ⑤: tên + chip (Đạt · Đang sửa · Chờ · Máy chưa sửa được · Đã đẩy) + dòng phụ («Máy đã tự sửa N chỗ» hoặc MỘT câu tiếng người khi máy hết cách) + nút «Viết lại tập n».
// THUẦN, không antd/react. Chip suy từ luật có sẵn: `episodeChip` (cần xem), `handoff`, `isPushable`, `chainProgress` — KHÔNG có luật song song. KHÔNG hiện «lần k/3» (không đếm lần ở FE; chốt 26/10).
// «N chỗ» = số CẢNH KHÁC NHAU trong các vòng máy sửa ĐƯỢC NHẬN của biên lai C2 lúc sinh (`repairReceiptOf`); vắng dữ liệu ⇒ 0 ⇒ ẩn dòng. Khi BE 3j2 có `resolvedFindingKeys` thì đổi nguồn.
import type { EpisodeScriptRecord, OutlineEpisode } from '../../../types/adaptation';
import type { ChainProgressView, WorkbenchAutoRepair, WorkbenchEpisode } from '../../../types/adaptationWorkbench';
import { autoRepairFixingOf, autoRepairNoteOf, autoRepairResolvedCount } from './autoRepairView';
import { repairReceiptOf } from './autoFixView';
import { isPushable } from './handoffBatchView';
import { criticCardOf } from './criticReceiptView';
import { friendlyReasonText } from './jobErrorText';
import { recheckSentence } from './recheckView';
import { episodeChip, filterCounts, matchesFilter } from './scriptStageView';
import type { EpisodeFilter, FilterCtx } from './scriptStageView';

// ─── «Máy đã tự sửa N chỗ» ──────────────────────────────────────────────────

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const strs = (x: unknown): string[] => (Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string' && s !== '') : []);

/** Số chỗ máy đã sửa trong bản đang giữ của tập; 0 ⇒ không hiện dòng. Người đã hoàn tác bản máy sửa ⇒ 0. */
export const fixedPlacesOf = (wb: Pick<WorkbenchEpisode, 'written' | 'autoFix'> | undefined, script: Pick<EpisodeScriptRecord, 'script'> | null | undefined): number => {
  if (!wb || wb.written !== 'draft' || !script) return 0;
  if (wb.autoFix?.state === 'undone') return 0;
  // 3j2b-2 (lượt 5): MỘT nguồn, không cộng (BE có thể ghi cả hai ⇒ đếm đôi): ưu tiên số lỗi máy đã gỡ ở lần bấm «Để máy sửa» (`qualityReview.autoRepair.resolvedFindingKeys`); vắng / rỗng ⇒ số cảnh sửa lúc sinh tập.
  return autoRepairResolvedCount(script) || placesAtWriteOf(script);
};
const placesAtWriteOf = (script: Pick<EpisodeScriptRecord, 'script'>): number => {
  const receipt = repairReceiptOf(script);
  if (!receipt) return 0;
  const chain = isObj(receipt.chain) ? receipt.chain : null;
  if (chain && Array.isArray(chain.rounds)) {
    const ids = new Set<string>();
    (chain.rounds as unknown[]).forEach((r) => { if (isObj(r) && r.accepted === true) strs(r.changedScenes).forEach((id) => ids.add(id)); });
    return ids.size;
  }
  const repair = isObj(receipt.repair) ? receipt.repair : null;
  return repair && repair.accepted === true ? new Set(strs(repair.changedScenes)).size : 0;
};

export interface AutoFixTotals { places: number; episodes: Array<{ ep: number; places: number }> }
/** Tổng cho dòng «Máy đã tự sửa N chỗ ở M tập — Xem tập n» trong «Chi tiết ▸» của dải. */
export const autoFixTotals = (episodes: ReadonlyArray<WorkbenchEpisode> | null | undefined, scripts: ReadonlyArray<EpisodeScriptRecord> | null | undefined): AutoFixTotals => {
  const byEp = new Map((scripts ?? []).map((s) => [s.episode, s]));
  const list = (episodes ?? []).map((e) => ({ ep: e.ep, places: fixedPlacesOf(e, byEp.get(e.ep)) })).filter((x) => x.places > 0).sort((a, b) => a.ep - b.ep);
  return { places: list.reduce((n, x) => n + x.places, 0), episodes: list };
};

// ─── Tập đang được máy sửa ──────────────────────────────────────────────────

/** Tập ⑤ mà BE báo đang ở vòng SỬA / chấm lại (RUNNING + REPAIR | REJUDGE, hoặc vòng ≥ 1). Lượt chấm đầu (JUDGE, vòng 0) chưa phải «đang sửa». */
export const fixingEpisodesOf = (cp: ChainProgressView | null | undefined): Set<number> => {
  const out = new Set<number>();
  (cp?.units ?? []).forEach((u) => {
    if (u.stage !== 'script' || u.unitKind !== 'episode' || typeof u.unitIndex !== 'number' || u.state !== 'RUNNING') return;
    if (u.livePhase === 'REPAIR' || u.livePhase === 'REJUDGE' || (typeof u.liveRound === 'number' && u.liveRound > 0)) out.add(u.unitIndex);
  });
  return out;
};

// ─── Dòng tập ───────────────────────────────────────────────────────────────

export type RowChipKey = 'pass' | 'fixing' | 'wait' | 'gaveUp' | 'review' | 'recheck' | 'pushed';
export interface RowChip { key: RowChipKey; label: string; tone: 'ok' | 'run' | 'todo' | 'bad' | 'warn' | 'info' }
export interface EpisodeRowView {
  ep: number;
  name: string;
  chip: RowChip;
  /** «Máy đã tự sửa N chỗ» hoặc null. */
  fixLine: string | null;
  fixPlaces: number;
  /** MỘT câu tiếng người khi tập cần xem / máy hết cách; null khi ổn. */
  reason: string | null;
  /** Lời giám khảo C2 NGUYÊN VĂN (đã qua `friendlyReasonText`/`redactMoney`) — CHỈ trang tập hiện (khối gập); dòng tập chỉ dùng `reason` cố định. */
  judge: string | null;
  /** 3k-c lượt 2 — MỘT lý do NGẮN (≤ 1 dòng ở cột 280px) hiện dưới dòng tập có lỗi; null khi ổn. Câu đầy đủ là `reason`. */
  short: string | null;
  needsLook: boolean;
  rewrite: { label: string } | null;
}

export interface EpisodeRowInput {
  row: OutlineEpisode;
  wb: WorkbenchEpisode | undefined;
  script?: EpisodeScriptRecord;
  /** Chuỗi «Truyện mới» ĐANG CHẠY (`chainRunningOf`): chỉ để chọn nhãn «Chờ» (đã dừng / xong ⇒ «Chưa viết»). KHÔNG còn là bằng chứng «máy đã thử sửa». */
  chain: boolean;
  /** BE báo tập đang được máy sửa (`fixingEpisodesOf`). */
  fixing: boolean;
  /** 3j2b-2 (D8): `workbench.autoRepair` (vắng khi cờ tắt ⇒ dòng y như trước). */
  auto?: WorkbenchAutoRepair | null;
}

const CHIP = {
  pass: { key: 'pass', label: 'Đạt', tone: 'ok' } as RowChip,
  pushed: { key: 'pushed', label: 'Đã đẩy', tone: 'info' } as RowChip,
  fixing: { key: 'fixing', label: 'Đang sửa', tone: 'run' } as RowChip,
  gaveUp: { key: 'gaveUp', label: 'Máy chưa sửa được', tone: 'bad' } as RowChip,
  review: { key: 'review', label: 'Cần xem', tone: 'warn' } as RowChip,
  recheck: { key: 'recheck', label: 'Cần kiểm lại', tone: 'warn' } as RowChip,
};
const WAIT_LABEL_CHAIN = 'Chờ';
const WAIT_LABEL_MANUAL = 'Chưa viết';
export const NOT_PUSHED_TAIL = 'Tập này sẽ không được đẩy lần này.';
/** Câu CỐ ĐỊNH cho dòng tập (không chứa chữ giám khảo — lời đó ở trang tập). */
export const GAVE_UP_SENTENCE = 'Máy đã thử sửa nhưng tập này còn một chỗ chưa ổn — mở tập để xem.';
export const CRITIC_SENTENCE = 'Giám khảo AI còn lỗi nặng ở tập này — mở tập để xem.';
export const NOT_PUSHABLE_SENTENCE = 'Tập này chưa đẩy được lần này — mở tập để xem lý do.';
/** Biên lai giám khảo là của bản CŨ (`criticFreshness` ≠ CURRENT): câu chính không được nói «còn lỗi» như thể còn đúng. */
export const STALE_CRITIC_SENTENCE = 'Tập đã đổi sau lần chấm — nên kiểm lại.';
export const SHORT = {
  failed: 'Lần viết gần nhất hỏng', stale: 'Đã đổi sau lần chấm', gaveUp: 'Máy chưa sửa xong lỗi', critic: 'Còn lỗi nặng', continuity: 'Nối tập chưa khớp', reader: 'AI đọc cả mùa chặn',
  orphan: 'Ngoài khung xương', other: 'Cần bạn xem', recheck: 'Sửa xong, cần chấm lại', blocked: 'Chưa đẩy được',
} as const;

/** Biên lai giám khảo đã cũ: người sửa / áp Trợ lý sau lần chấm (BE tính `criticFreshness` lúc đọc). */
export const criticStaleOf = (script: Pick<EpisodeScriptRecord, 'criticFreshness'> | null | undefined): boolean =>
  script?.criticFreshness === 'PARTLY_CHANGED' || script?.criticFreshness === 'CHANGED';

/** MỘT dòng tóm tắt tiếng Việt cho khối biên lai giám khảo đang gập (trang tập): không CRITICAL / MAJOR, không tên cảnh. Chi tiết nằm sau «Xem chi tiết». */
export const criticSummaryLine = (wb: Pick<WorkbenchEpisode, 'criticErrors'>, script: Pick<EpisodeScriptRecord, 'script' | 'violations' | 'criticFreshness'> | null | undefined): string => {
  if (criticStaleOf(script)) return 'Biên lai của giám khảo là của bản cũ — tập đã đổi sau lần chấm.';
  const n = criticCardOf(script)?.criticalLeft || wb.criticErrors || 0;
  return n > 0 ? `Giám khảo AI còn ${n} lỗi nặng ở bản lúc viết.` : 'Giám khảo AI còn lỗi nặng ở bản lúc viết.';
};

const sentence = (s: string): string => (/[.!?…]$/.test(s) ? s : `${s}.`);
const nameOf = (row: Pick<OutlineEpisode, 'synopsis'>, wb: Pick<WorkbenchEpisode, 'title'> | undefined): string => {
  const t = wb?.title?.trim();
  if (t) return t;
  const syn = (row.synopsis ?? '').trim();
  return syn.length > 60 ? `${syn.slice(0, 60)}…` : syn;
};

/** Giám khảo C2 còn lỗi nặng ở tập (BE đã tính: `criticBlocked`, `criticErrors`, đích còn lại). Cũng quyết định trang tập có MỞ SẴN danh sách lỗi giám khảo hay không (3k-c). */
export const criticLeft = (wb: WorkbenchEpisode): boolean => wb.criticBlocked === true || wb.criticErrors > 0 || (wb.c2Unresolved ?? []).some((u) => u?.target === true);
/**
 * Máy đã thử sửa CHÍNH TẬP NÀY — bằng chứng từng tập (không dùng cờ cấp truyện «có chuỗi»): `c2StopReason`, `autoFix`, `c2Unresolved`, hoặc biên lai vòng sửa (`repair`) của bản lúc sinh.
 * Chỉ khi đó mới được nói «Máy chưa sửa được».
 */
const machineTried = (wb: WorkbenchEpisode, script: EpisodeScriptRecord | undefined): boolean => {
  if (wb.c2StopReason !== undefined || !!wb.autoFix || (wb.c2Unresolved ?? []).length > 0) return true;
  const receipt = repairReceiptOf(script);
  return !!receipt && isObj(receipt.repair);
};

/**
 * LUẬT «BE khoá đẩy tập này» — DÙNG CHUNG với dải (`compactBandView.doneFacts`): BE cũ (không có `eligibleForPush`) ⇒ KHÔNG khoá (đường đẩy cũ);
 * có cờ mà không đẩy được (`false`, hoặc `true` nhưng thiếu băm) ⇒ khoá. Tập đã khớp Chapters không cần đẩy ⇒ không tính.
 */
export const pushBlockedOf = (wb: Pick<WorkbenchEpisode, 'written' | 'handoff' | 'eligibleForPush' | 'contentSha'>): boolean =>
  wb.written === 'draft' && wb.handoff !== 'up_to_date' && wb.eligibleForPush !== undefined && !isPushable(wb);

/** Lời giám khảo NGUYÊN VĂN của lỗi còn lại (đích trước) — chỉ cho trang tập. */
const judgeText = (wb: WorkbenchEpisode): string | null => {
  const list = wb.c2Unresolved ?? [];
  const pick = list.find((u) => u?.target === true && !!u.message?.trim()) ?? list.find((u) => !!u?.message?.trim());
  return pick ? friendlyReasonText(pick.message) : null;
};

export const episodeRowOf = (i: EpisodeRowInput): EpisodeRowView => {
  const { row, wb, script, chain, fixing } = i;
  const note = autoRepairNoteOf(i.auto, row.ep);
  const name = nameOf(row, wb);
  const places = fixedPlacesOf(wb, script);
  const fixLine = places > 0 ? `Máy đã tự sửa ${places} chỗ` : null;
  const mk = (chip: RowChip, extra: Partial<EpisodeRowView> = {}): EpisodeRowView => ({ ep: row.ep, name, chip, fixLine: null, fixPlaces: places, reason: null, judge: null, short: null, needsLook: chip.key === 'gaveUp' || chip.key === 'review' || chip.key === 'recheck', rewrite: null, ...extra });

  if (fixing || autoRepairFixingOf(i.auto).includes(row.ep)) return mk(CHIP.fixing);
  if (!wb || wb.written === 'none') return mk({ key: 'wait', label: chain ? WAIT_LABEL_CHAIN : WAIT_LABEL_MANUAL, tone: 'todo' });
  if (episodeChip(wb).key === 'review') {
    const tail = isPushable(wb) ? '' : ` ${NOT_PUSHED_TAIL}`;
    const rewrite = { label: `Viết lại tập ${row.ep}` };
    if (wb.written === 'failed') return mk(CHIP.review, { reason: 'Lần viết gần nhất bị hỏng — chưa có kịch bản dùng được.', short: SHORT.failed, rewrite });
    if (criticLeft(wb)) {
      // 3j2b-2: máy GIỮ NGUYÊN vì người đã chỉnh / tập đã đẩy ⇒ nói đúng điều đó (thắng cả «biên lai cũ»: chính việc người chỉnh làm biên lai cũ).
      if (note && (note.kind === 'edited' || note.kind === 'pushed')) return mk(CHIP.review, { reason: note.sentence, short: note.short, judge: judgeText(wb), rewrite });
      // Biên lai cũ (tập đã đổi sau lần chấm): KHÔNG nói «còn lỗi» / «máy chưa sửa được» như thể còn đúng; cũng không đưa «Viết lại» (ghi đè bản người sửa) — chỉ «nên kiểm lại».
      if (criticStaleOf(script)) return mk(CHIP.recheck, { reason: STALE_CRITIC_SENTENCE, short: SHORT.stale });
      // 3j2b-2: hết lượt / dừng vì giới hạn ở lần bấm «Để máy sửa» ⇒ MỘT câu của D8 (máy đã thử ⇒ «Máy chưa sửa được») + «Viết lại tập n».
      if (note) return mk(CHIP.gaveUp, { reason: note.sentence, short: note.short, judge: judgeText(wb), rewrite });
      const gaveUp = machineTried(wb, script);
      return mk(gaveUp ? CHIP.gaveUp : CHIP.review, { reason: `${gaveUp ? GAVE_UP_SENTENCE : CRITIC_SENTENCE}${tail}`, short: gaveUp ? SHORT.gaveUp : SHORT.critic, judge: judgeText(wb), rewrite });
    }
    if (wb.continuity === 'dirty') return mk(CHIP.review, { reason: sentence(friendlyReasonText(wb.continuityReason) || 'Nối tập chưa khớp'), short: SHORT.continuity });
    if (wb.readerBlocking > 0) return mk(CHIP.review, { reason: 'AI đọc cả mùa còn lỗi chặn ở tập này — mở tập để xem.', short: SHORT.reader });
    if (!wb.inOutline) return mk(CHIP.review, { reason: 'Tập có kịch bản nhưng không nằm trong danh sách tập của khung xương.', short: SHORT.orphan });
    return mk(CHIP.review, { reason: 'Tập này cần bạn xem — mở tập để biết lý do.', short: SHORT.other });
  }
  // F1: BE khoá đẩy mà không có lỗi giám khảo (nguồn tên chưa rõ, tập trước chưa sẵn…) hoặc cần kiểm lại (3j0) ⇒ KHÔNG «Đạt» — cùng luật với câu của dải.
  if (needsRecheckOf(wb)) {
    const base = recheckSentence([{ ep: row.ep, stale: wb.qualityReady?.c2 === 'STALE' }]);
    const kept = note && (note.kind === 'edited' || note.kind === 'pushed') ? note : null;   // người đã chỉnh ⇒ máy không đụng tới
    if (note?.kind === 'skipped') return mk(CHIP.recheck, { reason: note.sentence, short: note.short });   // R5: máy thiếu thông tin để kiểm ⇒ MỘT câu
    return mk(CHIP.recheck, { reason: kept ? `${base}. ${kept.sentence}` : base, short: kept ? kept.short : SHORT.recheck });
  }
  if (pushBlockedOf(wb)) return mk(CHIP.review, { reason: NOT_PUSHABLE_SENTENCE, short: SHORT.blocked });
  if (wb.handoff === 'up_to_date') return mk(CHIP.pushed, { fixLine, short: fixLine });
  return mk(CHIP.pass, { fixLine, short: fixLine });
};

export interface EpisodeRowsInput {
  rows: ReadonlyArray<OutlineEpisode>;
  episodes: ReadonlyArray<WorkbenchEpisode>;
  scripts: ReadonlyArray<EpisodeScriptRecord>;
  chain: boolean;
  chainProgress?: ChainProgressView | null;
  /** 3j2b-2: `workbench.autoRepair`. */
  autoRepair?: WorkbenchAutoRepair | null;
}
export const episodeRowsOf = (i: EpisodeRowsInput): EpisodeRowView[] => {
  const wbBy = new Map(i.episodes.map((e) => [e.ep, e]));
  const scBy = new Map(i.scripts.map((s) => [s.episode, s]));
  const fixing = fixingEpisodesOf(i.chainProgress);
  return i.rows.map((row) => episodeRowOf({ row, wb: wbBy.get(row.ep), script: scBy.get(row.ep), chain: i.chain, fixing: fixing.has(row.ep), auto: i.autoRepair }));
};

// ─── Lọc «Chỉ tập cần xem» (chỉ khi danh sách dài) ──────────────────────────

/** Quá ngần này tập thì mới có ô lọc (chốt 26/10, câu 5). */
export const EPISODE_FILTER_THRESHOLD = 8;
export const showNeedsLookFilter = (n: number): boolean => n > EPISODE_FILTER_THRESHOLD;
export const filterNeedsLook = (rows: ReadonlyArray<EpisodeRowView>, only: boolean): EpisodeRowView[] => (only ? rows.filter((r) => r.needsLook) : [...rows]);

/** Chuỗi «Truyện mới» đang chạy bước ⑤ mà đã có tập viết ra ⇒ hiện BẢNG TẬP (không phải hộp «máy đang chạy»): người dùng thấy tập nào Đạt / Đang sửa / Chờ. Chưa có tập nào ⇒ vẫn là hộp. */
export const scriptTableDuringChain = (episodes: ReadonlyArray<Pick<WorkbenchEpisode, 'written'>> | null | undefined): boolean => (episodes ?? []).some((e) => e.written !== 'none');

/** Chuỗi «Truyện mới» ĐANG CHẠY / chờ người (không phải đã XONG hay đã DỪNG): trước khi lưu khung xương (`newStoryRun`) hoặc `appendRun` NEW_STORY chưa DONE/CANCELLED. */
export const chainRunningOf = (w: { appendRun?: { kind?: string; status?: string } | null; newStoryRun?: unknown } | null | undefined): boolean => {
  if (!w) return false;
  const run = w.appendRun ?? null;
  if (run) return run.kind === 'NEW_STORY' && run.status !== 'DONE' && run.status !== 'CANCELLED';
  return !!w.newStoryRun;
};

/** Lý do cấp CHẶNG không thuộc tập nào ⇒ hiện ở ĐẦU vùng nội dung của ⑤ (lý do của từng tập nằm ở chip / trang tập). */
export const stageLevelNeeds = <T extends { ep?: number }>(needs: ReadonlyArray<T>): T[] => needs.filter((n) => n.ep === undefined);

// ─── 3k-c: tập mặc định khi vào ⑤ + luật dùng chung ─────────────────────────

/**
 * C2 (3k-c) — tập mở khi vào ⑤. THUẦN. `urlEp` hợp lệ (có trong `rows`) ⇒ thắng. Không thì tập ĐẦU TIÊN đang có lỗi chặn (chip «Máy chưa sửa được» / «Cần xem» / «Cần kiểm lại»),
 * không có thì tập đầu tiên; không có tập nào ⇒ null. «Đang sửa» và «Chờ» KHÔNG phải lỗi.
 * «Chốt một lần» nằm ở người gọi (ScriptStage): chỉ gọi khi URL chưa có `ep` hợp lệ rồi GHI kết quả vào URL; sau đó không bao giờ tính lại.
 */
export const defaultOpenEpisodeOf = (rows: ReadonlyArray<Pick<EpisodeRowView, 'ep' | 'needsLook'>>, urlEp: number | null): number | null => {
  if (urlEp !== null && rows.some((r) => r.ep === urlEp)) return urlEp;
  return rows.find((r) => r.needsLook)?.ep ?? rows[0]?.ep ?? null;
};

/** Chặn đẩy vì «Cần kiểm lại» (3j0): BE `qualityReady.needsRecheck`. Tập đã khớp Chapters không cần. DÙNG CHUNG: chip dòng tập · dấu ✓ ở cột bước (SA N5). */
export const needsRecheckOf = (wb: Pick<WorkbenchEpisode, 'handoff' | 'qualityReady'>): boolean => wb.handoff !== 'up_to_date' && wb.qualityReady?.needsRecheck === true;

/**
 * Bộ lọc của danh sách tập ở cột trái: «Cần xem» đọc CHÍNH chip của dòng (`needsLook` — gồm Máy chưa sửa được · Cần xem · Cần kiểm lại) để số «n cần xem» và chip không lệch nhau;
 * các bộ lọc khác (Chưa viết · Đã viết · Chapters · Tập mới) giữ luật `matchesFilter`.
 */
export const matchesListFilter = (wb: WorkbenchEpisode, row: Pick<EpisodeRowView, 'needsLook'> | undefined, f: EpisodeFilter, ctx?: FilterCtx): boolean =>
  (f === 'review' && row ? row.needsLook : matchesFilter(wb, f, ctx));
export const listFilterCounts = (episodes: WorkbenchEpisode[], rows: ReadonlyMap<number, Pick<EpisodeRowView, 'needsLook'>>, ctx?: FilterCtx): Record<EpisodeFilter, number> =>
  ({ ...filterCounts(episodes, ctx), review: episodes.filter((e) => matchesListFilter(e, rows.get(e.ep), 'review', ctx)).length });

/**
 * Cổng của việc «chốt tập mặc định» (ScriptStage gọi mỗi lần deps đổi, nhưng chỉ ghi URL MỘT lần):
 *  · `wait`   — chưa đủ dữ liệu (workbench chưa tải / chưa có dòng tập — lúc đó mọi dòng đọc «Chờ» và sẽ ghi nhầm `ep=1`) hoặc chuỗi đang chạy mà chưa có tập nào viết (thân bước là hộp «máy đang chạy»);
 *  · `skip`   — URL đã có `ep` hợp lệ, hoặc có ý định «Xem tập n» đang chờ (nó tự ghi URL): không giành, và coi như đã chốt;
 *  · `commit` — tính `defaultOpenEpisodeOf` rồi ghi vào URL.
 */
export const defaultEpisodeGate = (i: { workbenchReady: boolean; rowCount: number; chainRunning: boolean; anyWritten: boolean; urlEpValid: boolean; intentOpensEp: boolean }): 'wait' | 'skip' | 'commit' => {
  if (!i.workbenchReady || i.rowCount === 0 || (i.chainRunning && !i.anyWritten)) return 'wait';
  return i.urlEpValid || i.intentOpensEp ? 'skip' : 'commit';
};

/** Ở TRANG TẬP người dùng đang ở chính tập đó ⇒ bỏ vế «— mở tập để xem / để biết lý do» của câu dòng tập (câu trên bảng/cột vẫn giữ). */
export const onPageSentence = (reason: string | null | undefined): string | null =>
  (reason == null ? null : reason.replace(/\s*—\s*mở tập để (?:xem|biết)(?: lý do)?(?=\.)/g, ''));

/** Trang tập: lỗi của giám khảo (`critic` / `critic_blocked` — tiếng Anh kỹ thuật, theo từng cảnh, kèm «Phương án (tốn AI)») chỉ hiện sau «Xem chi tiết»; việc khác (nối tập, AI đọc cả mùa…) giữ nguyên. Tập không còn lỗi giám khảo ⇒ không gập. */
export const splitCriticReasons = <T extends { kind: string }>(reasons: ReadonlyArray<T>, criticOpen: boolean): { folded: T[]; plain: T[] } => {
  const isCritic = (r: T) => r.kind === 'critic' || r.kind === 'critic_blocked';
  return criticOpen ? { folded: reasons.filter(isCritic), plain: reasons.filter((r) => !isCritic(r)) } : { folded: [], plain: [...reasons] };
};

/** Khối «Cảnh báo của kịch bản» dưới hàng «Cảnh 1 · Cảnh 2…»: lỗi giám khảo (`CRITIC_FINDING`, tiếng Anh, kèm «Phương án») đã gập vào «Xem chi tiết» của trang tập ⇒ bỏ khỏi khối này; cảnh báo khác giữ nguyên. */
export const foldCriticDefects = <T extends { gate: string }>(defects: ReadonlyArray<T>, fold: boolean): T[] =>
  (fold ? defects.filter((d) => d.gate !== 'CRITIC_FINDING') : [...defects]);

/** Cảnh báo giám khảo ở khối cảnh báo của kịch bản chỉ được ẩn khi khối «Xem chi tiết» thật sự chứa lỗi (`folded` ≥ 1); khối gập rỗng mà vẫn ẩn thì tập mất nút «Phương án» theo cảnh. */
export const hideCriticFindingsOf = (criticOpen: boolean, foldedCount: number): boolean => criticOpen && foldedCount > 0;
